import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { ReviewsPanel } from './reviews/ReviewsPanel';
import { studioBOn } from '@/lib/studioB/flag';
import { SIDE_WIDTH } from '@/lib/studioB/useRoomForBothSides';
import { bMove } from '@/lib/studioB/motion';

type ReviewsProps = React.ComponentProps<typeof ReviewsPanel>;

interface StudioReviewsPanelProps {
  open: boolean;
  conversationId: string;
  onClose: ReviewsProps['onClose'];
  onSeekTo: ReviewsProps['onSeekTo'];
  /** Direction B on a phone: a full-width sheet under the bar instead of a side column. */
  sheet?: boolean;
}

const SHEET = 'fixed inset-x-0 bottom-0 top-[calc(var(--b-topbar)+env(safe-area-inset-top))] z-40 flex flex-col overflow-hidden rounded-t-[var(--b-radius-sheet)] border-t border-b-line bg-b-panel';

/** The right panel for reviews from shared links. It mounts the reviews only while open. */
export const StudioReviewsPanel: React.FC<StudioReviewsPanelProps> = ({ open, conversationId, onClose, onSeekTo, sheet = false }) => {
  const b = studioBOn();
  if (sheet) {
    return open ? (
      <div data-b-surface="" data-sheet-panel="" className={SHEET}>
        <ReviewsPanel conversationId={conversationId} onClose={onClose} onSeekTo={onSeekTo} fill />
      </div>
    ) : null;
  }
  return (
    <motion.div
      {...(b ? { 'data-b-surface': '' } : {})}
      className={cn(
        b
          ? "flex-shrink-0 flex flex-col overflow-hidden"
          : "flex-shrink-0 flex flex-col studio-panel-elevated overflow-hidden border-l border-black/5 dark:border-white/5"
      )}
      initial={false}
      animate={{
        width: open ? (b ? SIDE_WIDTH : 340) : 0,
        opacity: open ? 1 : 0,
      }}
      transition={b ? bMove() : { type: "spring", stiffness: 300, damping: 30 }}
    >
      <div
        {...(b ? { 'data-floating-panel': '' } : {})}
        className={cn(
          "h-full transition-transform",
          b ? "duration-b-move ease-b motion-reduce:transition-none" : "duration-300 ease-out",
          open ? "translate-x-0" : "translate-x-full"
        )}
      >
        {open && (
          <ReviewsPanel
            conversationId={conversationId}
            onClose={onClose}
            onSeekTo={onSeekTo}
          />
        )}
      </div>
    </motion.div>
  );
};
